import React, {
  useEffect,
  useState,
} from "react";

import {
  FaBuilding,
  FaEnvelope,
  FaLink,
  FaPhone,
} from "react-icons/fa";

import {
  toAbsoluteAvatar,
} from "../utils/acteursAdmin.utils";

/* =========================================================
   GROUPES
   ========================================================= */

export function GroupBadges({
  groups = [],
}) {
  if (!groups.length) {
    return (
      <span className="text-xs italic text-slate-400">
        Aucun groupe
      </span>
    );
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {groups.map((group) => {
        const isResponsable =
          !!group?.ActeurGroup?.is_responsable;

        return (
          <span
            key={group.id}
            className={[
              "inline-flex items-center rounded-full px-2.5 py-1",
              "text-[11px] font-medium ring-1",

              isResponsable
                ? [
                    "bg-rose-50",
                    "text-rose-700",
                    "ring-rose-200",
                    "dark:bg-rose-500/10",
                    "dark:text-rose-300",
                    "dark:ring-rose-500/20",
                  ].join(" ")
                : [
                    "bg-slate-50",
                    "text-slate-700",
                    "ring-slate-200",
                    "dark:bg-slate-800",
                    "dark:text-slate-200",
                    "dark:ring-slate-700",
                  ].join(" "),
            ].join(" ")}
          >
            {isResponsable ? (
              <span className="mr-1 font-bold">
                R
              </span>
            ) : null}

            {group.nom}
          </span>
        );
      })}
    </div>
  );
}

/* =========================================================
   UTILISATEURS LIÉS
   ========================================================= */

export function LinkedUsers({
  users = [],
}) {
  if (!users.length) {
    return (
      <span className="inline-flex rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700 ring-1 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20">
        Aucun compte lié
      </span>
    );
  }

  return (
    <div className="space-y-1.5">
      {users.map((user) => (
        <div
          key={user.id}
          className="rounded-xl bg-sky-50 px-2.5 py-1.5 text-[11px] font-medium text-sky-800 dark:bg-sky-500/10 dark:text-sky-300"
        >
          <div className="flex items-center gap-2">
            <FaLink className="shrink-0 text-[10px]" />

            <span>
              {String(
                user.nom || "",
              ).toUpperCase()}{" "}
              {user.prenom || ""}
            </span>
          </div>

          {user.email ? (
            <div className="mt-0.5 truncate pl-[18px] opacity-80">
              {user.email}
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}

/* =========================================================
   AVATAR
   ========================================================= */

function ActorAvatar({
  src,
  alt,
}) {
  const [
    imageSource,
    setImageSource,
  ] = useState(
    toAbsoluteAvatar(src),
  );

  useEffect(() => {
    setImageSource(
      toAbsoluteAvatar(src),
    );
  }, [src]);

  return (
    <img
      className="h-12 w-12 rounded-2xl object-cover shadow-sm ring-1 ring-slate-200 dark:ring-slate-700"
      src={imageSource}
      alt={alt}
      onError={() =>
        setImageSource(
          "/img/user_avatar.jpg",
        )
      }
    />
  );
}

/* =========================================================
   IDENTITÉ
   ========================================================= */

export function ActorIdentity({
  acteur,
}) {
  return (
    <div className="flex items-start gap-3">
      <ActorAvatar
        src={acteur.photo_url}
        alt={`${acteur.nom || ""} ${
          acteur.prenom || ""
        }`}
      />

      <div className="min-w-0">
        <div className="text-[15px] font-semibold leading-5 text-slate-900 dark:text-white">
          {String(
            acteur.nom || "",
          ).toUpperCase()}{" "}
          {acteur.prenom || ""}
        </div>

        {acteur.licence_number ? (
          <div className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
            Licence :{" "}
            {acteur.licence_number}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/* =========================================================
   COORDONNÉES
   ========================================================= */

export function ContactBlock({
  acteur,
}) {
  return (
    <div className="space-y-2 text-[13px] text-slate-700 dark:text-slate-200">
      {acteur.email ? (
        <a
          href={`mailto:${acteur.email}`}
          className="flex items-start gap-2 break-all text-indigo-600 hover:underline dark:text-indigo-300"
        >
          <FaEnvelope className="mt-0.5 shrink-0 text-slate-400" />

          <span>{acteur.email}</span>
        </a>
      ) : null}

      {acteur.tel ? (
        <div className="flex items-start gap-2">
          <FaPhone className="mt-0.5 shrink-0 text-slate-400" />

          <span>{acteur.tel}</span>
        </div>
      ) : null}

      {!acteur.email &&
      !acteur.tel ? (
        <span className="text-xs italic text-slate-400">
          Aucune coordonnée
        </span>
      ) : null}
    </div>
  );
}

/* =========================================================
   CLUB ET GROUPE SOUHAITÉ
   ========================================================= */

export function ClubAndWish({
  acteur,
}) {
  return (
    <div className="space-y-2">
      {acteur.club_acteur ? (
        <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-[11px] font-medium text-slate-800 dark:bg-slate-800 dark:text-slate-200">
          <FaBuilding className="text-slate-500 dark:text-slate-400" />

          {acteur.club_acteur}
        </span>
      ) : (
        <div className="text-xs italic text-slate-400">
          Club : —
        </div>
      )}

      {acteur.groupe_souhaite ? (
        <span className="inline-flex rounded-full bg-amber-50 px-3 py-1 text-[11px] font-medium text-amber-800 dark:bg-amber-500/10 dark:text-amber-300">
          Souhait :{" "}
          {acteur.groupe_souhaite}
        </span>
      ) : null}
    </div>
  );
}